// ** React Imports
import React, { FC } from 'react'

// ** MUI Imports
import { Checkbox, Typography, Tooltip } from '@mui/material'
import { validateColor, classNames } from 'src/lib/utils/helpers'

interface FloCheckboxProps {
  value: boolean
  type?: 'SWITCH' | 'CHECKBOX'
  size?: 'sm' | 'md'
  name?: string
  label?: React.ReactNode | undefined
  setValue?: (checked: boolean) => void
  isDisabled?: boolean
  isIndeterminate?: boolean
  color?: string
  tooltipTitle?: string
  activeTrackClassName?: string
}

const SWITCH_SIZE_CONFIG = {
  sm: { track: 'h-5 w-9', thumb: 'h-4 w-4', thumbOn: 'translate-x-4' },
  md: { track: 'h-6 w-11', thumb: 'h-5 w-5', thumbOn: 'translate-x-5' }
}

const FloCheckbox: FC<FloCheckboxProps> = ({
  value,
  type = 'CHECKBOX',
  size = 'sm',
  name,
  label,
  setValue,
  isDisabled,
  isIndeterminate,
  color,
  tooltipTitle,
  activeTrackClassName
}) => {
  const switchSize = SWITCH_SIZE_CONFIG[size]
  return (
    <Tooltip title={tooltipTitle}>
      <div className='flex items-center'>
        {type === 'CHECKBOX' && (
          <>
            <Checkbox
              name={name}
              sx={{
                padding: '0px 6px 0px 0px',
                transform: 'scale(0.85)',
                color: color && validateColor(color) ? `${color} !important` : '#4D4D4D !important',
                opacity: isDisabled ? 0.5 : 1
              }}
              checked={value}
              indeterminate={isIndeterminate}
              disabled={Boolean(isDisabled)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (typeof setValue === 'undefined') return
                setValue((event.target as HTMLInputElement).checked)
              }}
            />
            <Typography variant='body1' sx={{ fontSize: '14px', color: isDisabled ? '#949494' : '#4d4d4d' }}>
              {label}
            </Typography>
          </>
        )}
        {type === 'SWITCH' && (
          <label
            className={classNames(
              'inline-flex select-none items-center relative',
              isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
            )}
          >
            <input
              type='checkbox'
              name={name}
              disabled={isDisabled}
              checked={value}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (typeof setValue === 'undefined') return
                setValue((event.target as HTMLInputElement).checked)
              }}
              className='peer sr-only'
            />
            <div
              className={classNames(
                'relative shrink-0 rounded-full',
                switchSize.track,
                'peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-100 peer-focus-visible:ring-offset-2'
              )}
            >
              <div
                className={classNames(
                  'block overflow-hidden rounded-full transition-colors duration-200',
                  switchSize.track,
                  isDisabled
                    ? value
                      ? activeTrackClassName
                        ? classNames(activeTrackClassName, 'opacity-40')
                        : 'bg-gray-700/40'
                      : 'bg-zinc-950/5 shadow-[inset_0_0_0_0.5px_rgba(9,9,11,0.12)] opacity-40'
                    : value
                    ? activeTrackClassName
                      ? classNames(activeTrackClassName, 'hover:brightness-95')
                      : 'bg-gray-700 hover:bg-gray-800'
                    : 'bg-zinc-950/5 shadow-[inset_0_0_0_0.5px_rgba(9,9,11,0.12)] hover:bg-zinc-950/10'
                )}
              />
              <div
                className={classNames(
                  'absolute left-0.5 top-0.5 rounded-full bg-white shadow-[0_1px_2px_0_rgba(10,13,18,0.06),0_1px_3px_0_rgba(10,13,18,0.10)] transition-transform duration-200',
                  switchSize.thumb,
                  value ? switchSize.thumbOn : ''
                )}
              />
            </div>
          </label>
        )}
      </div>
    </Tooltip>
  )
}

export default FloCheckbox
